import axios from "axios";
import env from "../config/env";

// Validimi i API_URL para se të krijohet axios instance
if (!env.API_URL) {
  console.error(
    "API_URL nuk është i konfiguruar! Kontrollo .env file dhe sigurohu që REACT_APP_API_URL është i vendosur."
  );
  console.error(
    "process.env.REACT_APP_API_URL:",
    process.env.REACT_APP_API_URL
  );
  throw new Error(
    "API_URL nuk është i konfiguruar. Ju lutem kontrolloni environment variables dhe rinisni development server."
  );
}

// Krijo axios instance me default config
const api = axios.create({
  baseURL: env.API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 120000, // 120 sekonda (2 minuta) - për sync operacionet që mund të marrin kohë
});

// Request interceptor - shton auth token në çdo request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    // Nëse është endpoint publik (check-setup, login, database-setup, settings-setup), mos kërko token
    const isPublicEndpoint =
      config.url?.includes("/check-setup") ||
      config.url?.includes("/login") ||
      (config.url?.includes("/database/") && config.url?.includes("-setup")) ||
      config.url?.includes("/settings-setup");
    const isSetupEndpoint =
      (config.url?.includes("/database/") && config.url?.includes("-setup")) ||
      (config.url?.includes("/settings") && config.method === "get");

    // Nëse ka token dhe nuk është endpoint publik, shto token
    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Nëse është FormData, mos override Content-Type - lëre axios ta vendosë automatikisht me boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - trajton errors centralizuar
api.interceptors.response.use(
  (response) => {
    // Nëse API kthen format standardizuar me success field
    if (response.data && typeof response.data.success === "boolean") {
      if (response.data.success) {
        return response;
      } else {
        // API kthen error në format standardizuar
        const error = new Error(
          response.data.message || "Një gabim ka ndodhur"
        );
        error.response = response;
        error.errors = response.data.errors;
        return Promise.reject(error);
      }
    }
    // Format i vjetër - kompatibilitet prapa
    return response;
  },
  (error) => {
    // Handle network errors - mos shfaq shumë notifikime
    if (!error.response) {
      const networkError = new Error(
        "Lidhja me serverin dështoi. Kontrollo lidhjen me internetin."
      );
      networkError.isNetworkError = true;
      // Mos shfaq notifikim për çdo network error - vetëm log
      console.warn("Network error:", error.message);
      return Promise.reject(networkError);
    }

    const { status, data } = error.response;

    // Handle specific error statuses
    switch (status) {
      case 401:
        // Nëse është endpoint publik (check-setup, database-setup, settings-setup), mos bëj logout
        const isPublicEndpoint =
          error.config?.url?.includes("/check-setup") ||
          (error.config?.url?.includes("/database/") &&
            error.config?.url?.includes("-setup")) ||
          error.config?.url?.includes("/settings-setup");
        const isSetupEndpoint =
          (error.config?.url?.includes("/database/") &&
            error.config?.url?.includes("-setup")) ||
          error.config?.url?.includes("/settings-setup") ||
          (error.config?.url?.includes("/settings") &&
            error.config?.method === "get");

        if (!isPublicEndpoint && !isSetupEndpoint) {
          // Unauthorized - logout user
          localStorage.clear();
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          error.message =
            data?.message || "Session ka skaduar. Ju lutem kyçuni përsëri.";
        } else {
          // Në setup mode ose endpoint publik, mos shfaq error për auth
          error.message = data?.message || "Gabim gjatë lidhjes me serverin.";
        }
        break;

      case 403:
        error.message = data?.message || "Nuk keni autorizim për këtë veprim.";
        break;

      case 404:
        error.message = data?.message || "Rekordi nuk u gjet.";
        break;

      case 422:
        // Validation errors
        error.message =
          data?.message || "Të dhënat e dërguara nuk janë valide.";
        error.errors = data?.errors || {};
        break;

      case 500:
        error.message =
          data?.message || "Gabim serveri. Ju lutem provoni më vonë.";
        break;

      default:
        error.message =
          data?.message || error.message || "Një gabim ka ndodhur.";
    }

    return Promise.reject(error);
  }
);

// API helper methods
export const apiService = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      // Nëse API kthen format standardizuar, kthe data field
      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },

  // POST request
  post: async (url, data, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url, data, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },

  // PATCH request
  patch: async (url, data, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload file
  upload: async (url, formData, onUploadProgress) => {
    try {
      // Nëse URL ka ?_method=PUT ose &_method=PUT, përdor POST me _method=PUT në FormData
      let actualUrl = url;
      let useMethodOverride = false;

      if (url.includes("?_method=PUT")) {
        actualUrl = url.split("?_method=PUT")[0];
        useMethodOverride = true;
      } else if (url.includes("&_method=PUT")) {
        actualUrl = url.split("&_method=PUT")[0];
        useMethodOverride = true;
      }

      // Nëse duhet të përdorim method override, shto _method=PUT në FormData
      if (useMethodOverride && formData instanceof FormData) {
        formData.append("_method", "PUT");
      }

      const config = {};

      // Mos vendos Content-Type manualisht - lëre axios ta vendosë automatikisht me boundary
      // Axios do ta vendosë automatikisht kur dërgohet FormData

      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress;
      }

      // Përdorim gjithmonë POST kur kemi FormData dhe method override
      // Laravel do ta lexojë _method=PUT nga FormData
      const response = await api.post(actualUrl, formData, config);

      return response.data?.data !== undefined
        ? response.data.data
        : response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
